import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import RenderCell from '@components/DataGrid/RenderCell';
import { defaultSortComparator, EditableCellRef, EditSaveScope } from '@components/DataGrid/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { EditableCell, resolveEditTargetRows } from '../types';
import { usePatchLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import TextField from '@forms/Components/TextInput';
import { LegType } from '@api/Trinium/types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';

const headerDisplayName = 'Seal #';

const sealNumberDefinition: GridColDefExtended<DispatchLeg> = {
  field: 'Seal Number',
  minWidth: 75,
  width: 90,
  headerName: 'Seal Number',
  headerDisplayName,
  renderHeader: () => <RenderHeader primaryText={headerDisplayName} />,
  renderCell: ({ row }) => (
    <RenderCell
      editable
      primarySlot={
        row.puSealNo && (
          <span
            className={styles.bold}
            title={row.puSealNo}>
            {row.puSealNo}
          </span>
        )
      }
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { puSealNo } = row;
        return caseInsensitiveIncludes(value, puSealNo);
      });
  },
  valueGetter: (_, row: DispatchLeg) => row.puSealNo ?? '',
  sortable: true,
  sortComparator: defaultSortComparator,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <SealEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

export default sealNumberDefinition;

const legHasSeal = (row: DispatchLeg) => !(row.legType && LegType[row.legType] === LegType.ECL);

const SealEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [patchLegs] = usePatchLegsMutation();
  const [sealVal, setSealVal] = useState(row.puSealNo);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));
  const isDisabled = useMemo(() => isLoading || !legHasSeal(row), [isLoading, row]);

  const saveHandler = useCallback(async (scope: EditSaveScope) => {
    // Only need to call API if they changed the value
    if (sealVal !== row.puSealNo) {
      const targetRows = resolveEditTargetRows(scope, row, selectedRows, legHasSeal);

      await patchLegs({
        terminalNumber,
        legIds: targetRows.map((targetRow) => targetRow.legId),
        leg: {
          puSealNo: sealVal
        }
      })
        .unwrap()
        .then((response) => {
          response.errors.forEach((error) => {
            appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
          });
        })
        .catch((error: unknown) => {
          appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
        });
    }
  }, [appDispatch, terminalNumber, patchLegs, sealVal, row, selectedRows]);

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <TextField
      variant='standard'
      value={sealVal}
      onChange={(event) => {
        setSealVal(event.target.value);
      }}
      focused
      disabled={isDisabled}
      customProps={{
        labelText: 'Seal #',
        isDirty: false,
        errorText: undefined,
        onClear: () => {
          setSealVal('');
        },
        disabledReason: isLoading ? 'Updating...' : isDisabled ? 'Seal Number is not available on an ECL Leg' : undefined
      }}
    />
  );
});
